import { withNamespaces } from "react-i18next";
import { Accordion } from "react-bootstrap";
import { Fragment, useState } from "react";
import {
    Badge,
    Button,
    Card,
    CardBody,
    Col,
    Row,
    Table,
    UncontrolledTooltip
} from "reactstrap";
import { EMPTY_LIST } from "src/common/constants";
import DateUtils from "src/services/utils/DateUtils";
import { INTERNATIONAL_DATE_FORMAT } from 'src/common/constants';
import { KYBScreeningDataSets } from "src/modules/3rd-party-management/constants";
import CopyableText from "../common/CopyableText";

const EMPTY_VALUE_BY_FIELD_TYPE = {
    text: "—",
    category: "—",
    id: "—",
    date: "N/A",
    number: "N/A",
    amount: "N/A",
    list: "Empty",
    unknown: "No Data",
};

const isEmptyValue = (value) => {
    if (value === null || value === undefined) {
        return true;
    }

    if (typeof value === "string" && value.trim() === "") {
        return true;
    }

    if (Array.isArray(value) && value.length === 0) {
        return true;
    }

    return false;
};

const getEmptyValueByType = (fieldType = "text", t) => {
    const label = EMPTY_VALUE_BY_FIELD_TYPE[fieldType] || EMPTY_VALUE_BY_FIELD_TYPE.unknown;
    return typeof t === "function" ? t(label) : label;
};

const displayFieldValue = (value, fieldType = "text", t) => {
    if (isEmptyValue(value)) {
        return getEmptyValueByType(fieldType, t);
    }

    if (Array.isArray(value)) {
        return value.join(", ");
    }

    return value;
};

const displayDateValue = (isoDate, dateUtils, t) => {
    if (isEmptyValue(isoDate)) {
        return getEmptyValueByType("date", t);
    }

    return dateUtils.convertDateToDate(isoDate, INTERNATIONAL_DATE_FORMAT);
};

const SectionHeader = ({ t, icon, title, count, bgColor }) => {
    return (
        <div className="d-flex align-items-center gap-2">
            <i className={`ri-${icon} font-size-24 fw-normal`} />
            <span>{t(title)}</span>
            <Badge 
                pill 
                color={bgColor} 
                className={`bg-${bgColor}`}
            >
                {parseInt(count || 0)}
            </Badge>
        </div>
    );
}

const sanitizeDomId = (value) =>
    String(value || "")
        .replace(/[^a-zA-Z0-9_-]/g, "-")
        .replace(/^-+|-+$/g, "") || "id";

const EvidenceTooltipTarget = ({ id, content, className, children, as: Component = "span" }) => {
    const [targetEl, setTargetEl] = useState(null);

    return (
        <>
            <Component
                id={id}
                ref={setTargetEl}
                className={className}
            >
                {children}
            </Component>
            {targetEl && content ? (
                <UncontrolledTooltip
                    target={targetEl}
                    trigger="hover"
                    container="body"
                    placement="top"
                >
                    {content}
                </UncontrolledTooltip>
            ) : null}
        </>
    );
};

const EvidencesTable = ({ t, evidences, idPrefix = "evidence", nested = false }) => {
    const dateUtils = new DateUtils();
    const safePrefix = sanitizeDomId(idPrefix);
    const sortedEvidences = [...(evidences || [])].sort((a, b) =>
        (b.capture_date_iso || "").localeCompare(a.capture_date_iso || "")
    );

    return (
        <Table
            responsive={!nested}
            className={`evidence-table${nested ? " evidence-table--nested" : ""}`}
        >            <thead>
                <tr>
                    <th>{t("Evidence ID")}</th>
                    <th>{t("Title")}</th>
                    <th>{t("Credibility")}</th>
                    <th>{t("Capture Date")}</th>
                    <th>{t("Publication Date")}</th>
                    <th>{t("Summary")}</th>
                    <th>{t("URL")}</th>
                </tr>
            </thead>

            <tbody>
                {sortedEvidences.length > 0 ? (
                    sortedEvidences.map((evidence) => {
                        const datasets = evidence?.datasets || [];
                        const evidenceId = sanitizeDomId(evidence.evidence_id);
                        const rowKey = `${safePrefix}-${evidenceId}`;
                        const summaryTargetId = `${rowKey}-summary`;
                        const summaryText = evidence.summary;

                        return (
                            <tr key={rowKey}>
                                <td>
                                    {evidence.evidence_id}
                                    <br />
                                    {datasets.map((datasetKey) => {
                                        const datasetTargetId = `${rowKey}-dataset-${sanitizeDomId(datasetKey)}`;
                                        const datasetTitle = KYBScreeningDataSets[datasetKey]?.title;
                                        const datasetDescription =
                                            KYBScreeningDataSets[datasetKey]?.description;

                                        return (
                                            <EvidenceTooltipTarget
                                                key={datasetTargetId}
                                                id={datasetTargetId}
                                                content={
                                                    datasetDescription
                                                        ? t(datasetDescription)
                                                        : null
                                                }
                                            >
                                                <Badge className="bg-info ms-1 me-1">
                                                    {t(datasetTitle)}
                                                </Badge>
                                            </EvidenceTooltipTarget>
                                        );
                                    })}
                                </td>
                                <td>
                                    <p>{evidence.title || t("No title available")}</p>
                                </td>
                                <td>{t(evidence.credibility)}</td>
                                <td>
                                    {evidence.capture_date_iso
                                        ? dateUtils.convertDateToDate(
                                              evidence.capture_date_iso,
                                              INTERNATIONAL_DATE_FORMAT
                                          )
                                        : null}
                                </td>
                                <td>
                                    {evidence.publication_date_iso
                                        ? dateUtils.convertDateToDate(
                                              evidence.publication_date_iso,
                                              INTERNATIONAL_DATE_FORMAT
                                          )
                                        : null}
                                </td>
                                <td>
                                    {summaryText ? (
                                        <EvidenceTooltipTarget
                                            as="p"
                                            id={summaryTargetId}
                                            content={summaryText}
                                            className="mb-0"
                                        >
                                            {summaryText}
                                        </EvidenceTooltipTarget>
                                    ) : (
                                        <p>—</p>
                                    )}
                                </td>
                                <td>
                                    <a
                                        href={evidence.original_url}
                                        target="_blank"
                                        rel="noreferrer"
                                    >
                                        {evidence.original_url}
                                    </a>
                                </td>
                            </tr>
                        );
                    })
                ) : (
                    <tr>
                        <td className="text-center" colSpan="7">
                            {t(EMPTY_LIST)}
                        </td>
                    </tr>
                )}
            </tbody>
        </Table>
    );
};

const RelatedEvidencesAccordion = ({
    t,
    evidenceIds,
    findings,
    accordionKey,
}) => {
    const [isOpen, setIsOpen] = useState(false);

    if (!evidenceIds?.length) {
        return null;
    }

    const evidenceIdSet = new Set(evidenceIds.map(String));
    const relatedEvidences = (findings || []).filter((evidence) =>
        evidenceIdSet.has(String(evidence.evidence_id))
    );

    return (
        <Accordion
            className="related-evidences-accordion mt-3"
            activeKey={isOpen ? accordionKey : null}
            onSelect={(eventKey) => setIsOpen(eventKey === accordionKey)}
        >
            <Accordion.Item eventKey={accordionKey}>
                <Accordion.Header>
                    <div className="related-evidences-accordion__header">
                        <i className="ri-file-list-3-line related-evidences-accordion__icon" />
                        <span className="related-evidences-accordion__title">
                            {t("Related Evidences")}
                        </span>
                        <Badge
                            pill
                            color="info"
                            className="bg-info related-evidences-accordion__count"
                        >
                            {relatedEvidences.length}
                        </Badge>
                    </div>
                </Accordion.Header>
                <Accordion.Body>
                    {isOpen ? (
                        <EvidencesTable
                            t={t}
                            evidences={relatedEvidences}
                            idPrefix={accordionKey}
                        />
                    ) : null}
                </Accordion.Body>
            </Accordion.Item>
        </Accordion>
    );
};

const getRelatedEvidences = (evidenceIds, findings) => {
    if (!evidenceIds?.length) {
        return [];
    }

    const evidenceIdSet = new Set(evidenceIds.map(String));
    return (findings || []).filter((evidence) =>
        evidenceIdSet.has(String(evidence.evidence_id))
    );
};

const EntriesWithRelatedEvidencesTable = ({
    t,
    entries,
    findings,
    sectionKey,
    columns,
    getEvidenceIds = (entry) => entry.evidenceIDs,
}) => {
    const [expandedRowKey, setExpandedRowKey] = useState(null);
    const columnCount = columns.length + 1;

    const toggleRow = (rowKey) => {
        setExpandedRowKey((current) => (current === rowKey ? null : rowKey));
    };

    return (
        <Table responsive striped className="pep-entries-table">
            <thead>
                <tr>
                    {columns.map((column) => (
                        <th key={column.key} style={column.headerStyle}>
                            {column.header}
                        </th>
                    ))}
                    <th style={{ width: "160px" }}>{t("Evidences")}</th>
                </tr>
            </thead>
            <tbody>
                {entries.map((entry, i) => {
                    const rowKey = `${sectionKey}-${i}`;
                    const isExpanded = expandedRowKey === rowKey;
                    const evidenceIds = getEvidenceIds(entry);
                    const evidenceCount = evidenceIds?.length || 0;
                    const relatedEvidences = isExpanded
                        ? getRelatedEvidences(evidenceIds, findings)
                        : [];

                    return (
                        <Fragment key={rowKey}>
                            <tr className={isExpanded ? "pep-entries-table__row--expanded" : ""}>
                                {columns.map((column) => (
                                    <td key={column.key}>
                                        {column.render(entry)}
                                    </td>
                                ))}
                                <td>
                                    {evidenceCount > 0 ? (
                                        <Button
                                            color="link"
                                            className="p-0 pep-entries-table__evidences-toggle"
                                            onClick={() => toggleRow(rowKey)}
                                        >
                                            {t("Evidences")} ({evidenceCount})
                                            <i
                                                className={`ms-1 ri-arrow-${
                                                    isExpanded ? "up" : "down"
                                                }-s-line`}
                                            />
                                        </Button>
                                    ) : (
                                        getEmptyValueByType("list", t)
                                    )}
                                </td>
                            </tr>

                            {isExpanded && (
                                <tr className="pep-entries-table__expand-row">
                                    <td colSpan={columnCount}>
                                        <div className="pep-entries-table__expand-content">
                                            <div className="d-flex align-items-center gap-2 mb-2">
                                                <i className="ri-file-list-3-line related-evidences-accordion__icon" />
                                                <strong>{t("Related Evidences")}</strong>
                                                <Badge
                                                    pill
                                                    color="info"
                                                    className="bg-info related-evidences-accordion__count"
                                                >
                                                    {relatedEvidences.length}
                                                </Badge>
                                            </div>
                                            <EvidencesTable
                                                t={t}
                                                evidences={relatedEvidences}
                                                idPrefix={`${rowKey}-evidences`}
                                                nested
                                            />
                                        </div>
                                    </td>
                                </tr>
                            )}
                        </Fragment>
                    );
                })}
            </tbody>
        </Table>
    );
};

const PepEntriesTable = EntriesWithRelatedEvidencesTable;

const Evidences = ({ t, data: {
    findings,
    monitoringID
}}) => {
    return (
        <Accordion.Item
            eventKey="0"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="article-line"
                    title="Evidence Articles"
                    count={findings?.length}
                    bgColor="info"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="0"
            >
                <EvidencesTable
                    t={t}
                    evidences={findings}
                    idPrefix="evidence-articles"
                />

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const SanctionEntries = ({ t, data: {
    sanEntries: entries,
    findings,
    monitoringID
}}) => {
    const sanctionsTotalCount = (entries?.current?.length || 0) +
        (entries?.former?.length || 0);

    const renderSanctionEvents = (events, sectionKey, sanctionIndex) => (
        <div className="mb-3">
            <strong>{t("Events")}:</strong>

            {events?.map((ev, j) => (
                <div
                    key={j}
                    className="border-start ps-3 mt-2"
                >
                    <div>
                        <strong>{t("Type")}:</strong> {displayFieldValue(ev.type, "category", t)}
                    </div>
                    <div>
                        <strong>{t("Date")}:</strong> {displayFieldValue(ev.dateISO, "date", t)}
                    </div>

                    <RelatedEvidencesAccordion
                        t={t}
                        evidenceIds={ev.evidenceIDs}
                        findings={findings}
                        accordionKey={`${sectionKey}-sanction-${sanctionIndex}-event-${j}-evidences`}
                    />
                </div>
            ))}
        </div>
    );

    return (
        <Accordion.Item
            eventKey="1"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="forbid-line"
                    title="Sanctions"
                    count={sanctionsTotalCount}
                    bgColor="danger"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="1"
            >
                {entries?.current?.length > 0 && (
                    <>
                        <h6 className="text-uppercase text-muted">
                            {t('Current Sanctions')}
                        </h6>

                        {entries.current.map((s, i) => (
                            <Card key={`current-${i}`}>
                                <CardBody>

                                    <div className="mb-2">
                                        <strong>{t('Sanction ID')}:</strong> {displayFieldValue(s.sanctionID, "id", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Measures')}:</strong>{" "}
                                        {displayFieldValue(s.measures, "list", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Regime Body')}:</strong> {displayFieldValue(s.regime?.body, "text", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Regime Name')}:</strong> {displayFieldValue(s.regime?.name, "text", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Origin')}:</strong> {displayFieldValue(s.regime?.origin, "text", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Regime Types')}:</strong>{" "}
                                        {displayFieldValue(s.regime?.types, "list", t)}
                                    </div>

                                    {renderSanctionEvents(s.events, "current", i)}

                                </CardBody>
                            </Card>
                        ))}
                    </>
                )}

                {entries?.former?.length > 0 && (
                    <>
                        <h6 className="text-uppercase text-muted mt-4">
                            {t('Former Sanctions')}
                        </h6>

                        {entries.former.map((s, i) => (
                            <Card key={`former-${i}`}>
                                <CardBody>

                                    <div className="mb-2">
                                        <strong>{t('Sanction ID')}:</strong> {displayFieldValue(s.sanctionID, "id", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Measures')}:</strong>{" "}
                                        {displayFieldValue(s.measures, "list", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Regime Body')}:</strong> {displayFieldValue(s.regime?.body, "text", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Regime Name')}:</strong> {displayFieldValue(s.regime?.name, "text", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Origin')}:</strong> {displayFieldValue(s.regime?.origin, "text", t)}
                                    </div>

                                    <div className="mb-2">
                                        <strong>{t('Regime Types')}:</strong>{" "}
                                        {displayFieldValue(s.regime?.types, "list", t)}
                                    </div>

                                    {renderSanctionEvents(s.events, "former", i)}
                                </CardBody>
                            </Card>
                        ))}
                    </>
                )}

                {sanctionsTotalCount === 0 && (
                    <div className="text-muted mb-3">
                        {t('No sanction records available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const RelEntries = ({ t, data: {
    relEntries: entries,
    findings,
    monitoringID
}}) => {
    const dateUtils = new DateUtils();

    return (
        <Accordion.Item
            eventKey="2"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="file-line"
                    title="Regulatory Enforcement"
                    count={entries?.length}
                    bgColor="warning"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="2"
            >
                {entries?.length > 0 ? (
                    entries.map((entry, i) => (
                        <Card key={i} className="mb-3">
                            <CardBody className="pt-0">
                                <div className="mb-2">
                                    <strong>{t('Category')}:</strong> {displayFieldValue(entry.category, "category", t)}
                                </div>
                                <div className="mb-2">
                                    <strong>{t('Subcategory')}:</strong> {displayFieldValue(entry.subcategory, "category", t)}
                                </div>

                                {entry.events?.map((ev, j) => (
                                    <div
                                        key={j}
                                        className="border-start ps-3 mt-2"
                                    >
                                        <div>
                                            <strong>{t('Type')}:</strong> {displayFieldValue(ev.type, "category", t)}
                                        </div>
                                        {ev.currencyCode && (
                                            <div>
                                                <strong>{t('Currency')}:</strong> {ev.currencyCode}
                                            </div>
                                        )}
                                        {ev.amount !== undefined && (
                                            <div>
                                                <strong>{t('Amount')}:</strong>{" "}
                                                {displayFieldValue(ev.amount, "amount", t)}
                                            </div>
                                        )}
                                        {ev.period && (
                                            <div>
                                                <strong>{t('Period')}:</strong>{" "}
                                                {ev.period.years}y {ev.period.months}m {ev.period.days}d
                                            </div>
                                        )}
                                        <div>
                                            <strong>{t('Date')}:</strong>{' '}
                                            {displayDateValue(ev.dateISO, dateUtils, t)}
                                        </div>
                                        {ev.evidenceIDs?.length > 0 && (
                                            <RelatedEvidencesAccordion
                                                t={t}
                                                evidenceIds={ev.evidenceIDs}
                                                findings={findings}
                                                accordionKey={`rel-entry-${i}-event-${j}-evidences`}
                                            />
                                        )}
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    ))
                ) : (
                    <div className="text-muted mb-3">
                        {t('No regulatory enforcement records available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const RreEntries = ({ t, data: {
    rreEntries: entries,
    findings,
    monitoringID
}}) => {
    const dateUtils = new DateUtils();

    return (
        <Accordion.Item
            eventKey="3"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="alert-line"
                    title="Reputational Risk Exposure"
                    count={entries?.length}
                    bgColor="secondary"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="3"
            >
                {entries?.length > 0 ? (
                    entries.map((entry, i) => (
                        <Card key={i} className="mb-3">
                            <CardBody className="pt-0">
                                <div className="mb-2">
                                    <strong>{t('Category')}:</strong> {displayFieldValue(entry.category, "category", t)}
                                </div>
                                <div className="mb-2">
                                    <strong>{t('Subcategory')}:</strong> {displayFieldValue(entry.subcategory, "category", t)}
                                </div>

                                {entry.events?.map((ev, j) => (
                                    <div
                                        key={j}
                                        className="border-start ps-3 mt-2"
                                    >
                                        <div>
                                            <strong>{t('Type')}:</strong> {displayFieldValue(ev.type, "category", t)}
                                        </div>
                                        <div>
                                            <strong>{t('Date')}:</strong>{' '}
                                            {displayDateValue(ev.dateISO, dateUtils, t)}
                                        </div>
                                        {ev.evidenceIDs?.length > 0 && (
                                            <RelatedEvidencesAccordion
                                                t={t}
                                                evidenceIds={ev.evidenceIDs}
                                                findings={findings}
                                                accordionKey={`rre-entry-${i}-event-${j}-evidences`}
                                            />
                                        )}
                                    </div>
                                ))}
                            </CardBody>
                        </Card>
                    ))
                ) : (
                    <div className="text-muted mb-3">
                        {t('No reputational risk exposure records available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const PoiEntries = ({ t, data: {
    poiEntries: entries,
    findings,
    monitoringID
}}) => {
    const dateUtils = new DateUtils();

    return (
        <Accordion.Item
            eventKey="4"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="user-star-line"
                    title="Positions of Interest"
                    count={entries?.length}
                    bgColor="primary"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="4"
            >
                {entries?.length > 0 ? (
                    entries.map((entry, i) => (
                        <Card key={i} className="mb-3">
                            <CardBody className="pt-0">
                                <div className="mb-2">
                                    <strong>{t('Category')}:</strong> {displayFieldValue(entry.category, "category", t)}
                                </div>

                                {entry.evidenceIDs?.length > 0 && (
                                    <div className="mb-2">
                                        <RelatedEvidencesAccordion
                                            t={t}
                                            evidenceIds={entry.evidenceIDs}
                                            findings={findings}
                                            accordionKey={`poi-entry-${i}-evidences`}
                                        />
                                    </div>
                                )}

                                {entry.positions?.length > 0 &&
                                    entry.positions.map((pos, j) => (
                                        <div key={j} className="border-start ps-3 mt-2">
                                            <div>
                                                <strong>{t('Position')}:</strong>{" "}
                                                {displayFieldValue(pos.position, "text", t)}
                                            </div>
                                            <div>
                                                <strong>{t('Segment')}:</strong>{" "}
                                                {displayFieldValue(pos.segment, "category", t)}
                                            </div>
                                            <div>
                                                <strong>{t('Country')}:</strong>{" "}
                                                {displayFieldValue(pos.countryISOCode, "text", t)}
                                            </div>
                                            <div>
                                                <strong>{t('Date From')}:</strong>{' '}
                                                {displayDateValue(pos.dateFromISO, dateUtils, t)}
                                            </div>
                                            <div>
                                                <strong>{t('Date To')}:</strong>{" "}
                                                {displayDateValue(pos.dateToISO, dateUtils, t)}
                                            </div>
                                        </div>
                                    ))}
                            </CardBody>
                        </Card>
                    ))
                ) : (
                    <div className="text-muted mb-3">
                        {t('No Positions of Interest records available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const InsEntries = ({ t, data: {
    insEntries: entries,
    findings,
    monitoringID
}}) => {
    const dateUtils = new DateUtils();

    return (
        <Accordion.Item
            eventKey="5"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="bank-line"
                    title="Insolvency Records"
                    count={entries?.length}
                    bgColor="warning"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="5"
            >
                {entries?.length > 0 ? (
                    entries.map((entry, i) => (
                        <Card key={i} className="mb-3">
                            <CardBody className="pt-0">
                                <Row>
                                    <Col sm="12" md="6" className="mb-2">
                                        <strong>{t('Type')}:</strong> {displayFieldValue(entry.type, "category", t)}
                                    </Col>
                                    <Col sm="12" md="6" className="mb-2">
                                        <strong>{t('Insolvency ID Number')}:</strong>{" "}
                                        {displayFieldValue(entry.insolvencyIDNumber, "id", t)}
                                    </Col>

                                    <Col sm="12" md="6" className="mb-2">
                                        <strong>{t('Solicitor')}:</strong>{" "}
                                        {displayFieldValue(entry.solicitor, "text", t)}
                                    </Col>
                                    <Col sm="12" md="6" className="mb-2">
                                        <strong>{t('Court')}:</strong>{" "}
                                        {displayFieldValue(entry.court, "text", t)}
                                    </Col>

                                    <Col sm="12" md="6" className="mb-2">
                                        <strong>{t('Petitioner')}:</strong>{" "}
                                        {displayFieldValue(entry.petitioner, "text", t)}
                                    </Col>
                                    <Col sm="12" md="6" className="mb-2">
                                        <strong>{t('Debt')}:</strong>{" "}
                                        {displayFieldValue(entry.debt, "amount", t)}
                                    </Col>

                                    <Col sm="12" md="12" className="mb-2">
                                        <strong>{t('Accountant In Bankruptcy Case Number')}:</strong>{" "}
                                        {displayFieldValue(
                                            entry.accountantInBankruptcyCaseNumber,
                                            "id",
                                            t
                                        )}
                                    </Col>

                                    {entry.evidenceIDs?.length > 0 && (
                                        <Col sm="12" md="12" className="mb-2">
                                            <RelatedEvidencesAccordion
                                                t={t}
                                                evidenceIds={entry.evidenceIDs}
                                                findings={findings}
                                                accordionKey={`ins-entry-${i}-evidences`}
                                            />
                                        </Col>
                                    )}

                                    <Col sm="12" md="12">
                                        <Table responsive striped>
                                            <thead>
                                                <tr>
                                                    <th>{t('Insolvency Start')}</th>
                                                    <th>{t('Insolvency End')}</th>
                                                    <th>{t('Hearing Date')}</th>
                                                    <th>{t('Petition Date')}</th>
                                                    <th>{t('Gazette Issue Date')}</th>
                                                    <th>{t('Award Date')}</th>
                                                    <th>{t('First Order Date')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                <tr>
                                                    <td>
                                                        {entry.insolvencyStartDateISO ? (
                                                            dateUtils.convertDateToDate(
                                                                entry.insolvencyStartDateISO,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )
                                                        ) : '--'}
                                                    </td>
                                                    <td>
                                                        {entry.insolvencyEndDateISO ? (
                                                            dateUtils.convertDateToDate(
                                                                entry.insolvencyEndDateISO,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )
                                                        ) : '--'}
                                                    </td>
                                                    <td>
                                                        {entry.hearingDateISO ? (
                                                            dateUtils.convertDateToDate(
                                                                entry.hearingDateISO,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )
                                                        ) : '--'}
                                                    </td>
                                                    <td>
                                                        {entry.presentedDateOfPetitionISO ? (
                                                            dateUtils.convertDateToDate(
                                                                entry.presentedDateOfPetitionISO,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )
                                                        ) : '--'}
                                                    </td>
                                                    <td>
                                                        {entry.gazetteIssueDateISO ? (
                                                            dateUtils.convertDateToDate(
                                                                entry.gazetteIssueDateISO,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )
                                                        ) : '--'}
                                                    </td>
                                                    <td>
                                                        {entry.awardDateISO ? (
                                                            dateUtils.convertDateToDate(
                                                                entry.awardDateISO,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )
                                                        ) : '--'}
                                                    </td>
                                                    <td>
                                                        {entry.firstOrderDateISO ? (
                                                            dateUtils.convertDateToDate(
                                                                entry.firstOrderDateISO,
                                                                INTERNATIONAL_DATE_FORMAT
                                                            )
                                                        ) : '--'}
                                                    </td>
                                                </tr>
                                            </tbody>
                                        </Table>
                                    </Col>
                                </Row>
                            </CardBody>
                        </Card>
                    ))
                ) : (
                    <div className="text-muted mb-3">
                        {t('No Insolvency records available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const DdEntries = ({ t, data: {
    ddEntries: entries,
    findings,
    monitoringID
}}) => {
    const dateUtils = new DateUtils();

    const ddColumns = [
        {
            key: "caseReference",
            header: t("Case Reference"),
            headerStyle: { width: "140px" },
            render: (entry) => displayFieldValue(entry.caseReference, "text", t),
        },
        {
            key: "reason",
            header: t("Reason"),
            render: (entry) => displayFieldValue(entry.reason, "text", t),
        },
        {
            key: "conduct",
            header: t("Conduct"),
            render: (entry) => displayFieldValue(entry.conduct, "text", t),
        },
        {
            key: "dateFrom",
            header: t("Date From"),
            headerStyle: { width: "120px" },
            render: (entry) => displayDateValue(entry.dateFromISO, dateUtils, t),
        },
        {
            key: "dateTo",
            header: t("Date To"),
            headerStyle: { width: "120px" },
            render: (entry) => displayDateValue(entry.dateToISO, dateUtils, t),
        },
    ];

    return (
        <Accordion.Item
            eventKey="6"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="file-warning-line"
                    title="Disqualified Directors"
                    count={entries?.length}
                    bgColor="danger"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="6"
            >
                {entries?.length > 0 ? (
                    <EntriesWithRelatedEvidencesTable
                        t={t}
                        entries={entries}
                        findings={findings}
                        sectionKey="dd"
                        columns={ddColumns}
                    />
                ) : (
                    <div className="text-muted mb-3">
                        {t('No Disqualified Director records available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const PepEntries = ({ t, data: {
    pepEntries: entries,
    findings,
    monitoringID
}}) => {
    const dateUtils = new DateUtils();

    const pepEntriesTotalCount = (entries?.current?.length || 0) +
        (entries?.former?.length || 0);

    const pepColumns = [
        {
            key: "country",
            header: t("Country"),
            render: (entry) => displayFieldValue(entry.countryISOCode, "text", t),
        },
        {
            key: "segment",
            header: t("Segment"),
            render: (entry) => displayFieldValue(entry.segment, "category", t),
        },
        {
            key: "position",
            header: t("Position"),
            render: (entry) => displayFieldValue(entry.position, "text", t),
        },
        {
            key: "dateFrom",
            header: t("Date From"),
            headerStyle: { width: "120px" },
            render: (entry) => displayDateValue(entry.dateFromISO, dateUtils, t),
        },
        {
            key: "dateTo",
            header: t("Date To"),
            headerStyle: { width: "120px" },
            render: (entry) => displayDateValue(entry.dateToISO, dateUtils, t),
        },
    ];

    return (
        <Accordion.Item
            eventKey="7"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="file-warning-line"
                    title={`${t('Politically Exposed Persons')} ${entries?.pepTier ? `(${entries.pepTier})` : ''}`}
                    count={pepEntriesTotalCount}
                    bgColor="info"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="7"
            >
                {/* CURRENT */}
                {entries?.current?.length > 0 && (
                    <>
                        <h6 className="text-uppercase text-muted mb-2">
                            {t('Current PEPs')}
                        </h6>
                        <EntriesWithRelatedEvidencesTable
                            t={t}
                            entries={entries.current}
                            findings={findings}
                            sectionKey="pep-current"
                            columns={pepColumns}
                        />
                    </>
                )}

                {/* FORMER */}
                {entries?.former?.length > 0 && (
                    <>
                        <h6 className="text-uppercase text-muted mt-3 mb-2">
                            {t('Former PEPs')}
                        </h6>
                        <EntriesWithRelatedEvidencesTable
                            t={t}
                            entries={entries.former}
                            findings={findings}
                            sectionKey="pep-former"
                            columns={pepColumns}
                        />
                    </>
                )}

                {pepEntriesTotalCount === 0 && (
                    <div className="text-muted mb-3">
                        {t('No PEP records available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const IndividualLinks = ({ t, data: {
    individualLinks: links,
    monitoringID
}}) => {
    return (
        <Accordion.Item
            eventKey="8"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="user-line"
                    title="Individual Links"
                    count={links?.length}
                    bgColor="secondary"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="8"
            >
                {links?.length > 0 ? (
                    <Table responsive striped className="compliance-links-table">
                        <thead>
                            <tr>
                                <th className="compliance-links-table__id-col">{t('ID')}</th>
                                <th className="compliance-links-table__id-col">{t('Monitoring ID')}</th>
                                <th>{t('Full Name')}</th>
                                <th>{t('Relationship')}</th>
                                <th
                                    style={{
                                        width: '120px'
                                    }}
                                >{t('Ownership')} %</th>
                                <th>{t('Datasets')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {links.map((ind, i) => (
                                <tr key={i}>
                                    <td className="compliance-links-table__id-col">
                                        <CopyableText
                                            value={ind.id}
                                            maxLength={12}
                                        />
                                    </td>
                                    <td className="compliance-links-table__id-col">
                                        <CopyableText
                                            value={ind.monitoringID}
                                            maxLength={12}
                                        />
                                    </td>
                                    <td>
                                        {[ind.firstName, ind.middleName, ind.lastName].filter(Boolean).join(" ")}
                                    </td>
                                    <td>{ind.relationship}</td>
                                    <td>{ind.ownershipPercentage}</td>
                                    <td>
                                        {ind.datasets?.map((datasetKey, index) => {
                                            return (
                                                <Badge
                                                    key={index}
                                                    id={`ind-row-dataset-${index}`}
                                                    className="bg-info ms-1 me-1"
                                                >
                                                    {t(KYBScreeningDataSets[datasetKey]?.title)}

                                                    <UncontrolledTooltip
                                                        target={`ind-row-dataset-${index}`}
                                                    >
                                                        {t(KYBScreeningDataSets[datasetKey]?.description)}
                                                    </UncontrolledTooltip>
                                                </Badge>
                                            )
                                        })}
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <div className="text-muted mb-3">
                        {t('No individual links available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const BusinessLinks = ({ t, data: {
    businessLinks: links,
    monitoringID
}}) => {
    return (
        <Accordion.Item
            eventKey="9"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="building-line"
                    title="Business Links"
                    count={links?.length}
                    bgColor="primary"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="9"
            >
                {links?.length > 0 ? (
                    links.map((business, i) => (
                        <Card key={i} className="mb-3">
                            <CardBody>
                                <Table striped responsive className="compliance-links-table">
                                    <thead>
                                        <tr>
                                            <th>{t('Name')}</th>
                                            <th className="compliance-links-table__id-col">{t('ID')}</th>
                                            <th className="compliance-links-table__id-col">{t('Monitoring ID')}</th>
                                            <th>{t('Relationship')}</th>
                                            <th>{t('Ownership')} %</th>
                                            <th>{t('Datasets')}</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        <tr>
                                            <td>{business.name}</td>
                                            <td className="compliance-links-table__id-col">
                                                <CopyableText
                                                    value={business.id}
                                                    maxLength={12}
                                                />
                                            </td>
                                            <td className="compliance-links-table__id-col">
                                                <CopyableText
                                                    value={business.monitoringID}
                                                    maxLength={12}
                                                />
                                            </td>
                                            <td>{business.relationship}</td>
                                            <td>{business.ownershipPercentage}</td>
                                            <td>
                                                {business.datasets?.map((datasetKey, index) => {
                                                    return (
                                                        <Badge
                                                            key={index}
                                                            id={`business-row-dataset-${index}`}
                                                            className="bg-info ms-1 me-1"
                                                        >
                                                            {t(KYBScreeningDataSets[datasetKey]?.title)}

                                                            <UncontrolledTooltip
                                                                target={`business-row-dataset-${index}`}
                                                            >
                                                                {t(KYBScreeningDataSets[datasetKey]?.description)}
                                                            </UncontrolledTooltip>
                                                        </Badge>
                                                    )
                                                })}
                                            </td>
                                        </tr>
                                    </tbody>
                                </Table>

                                {business.individualLinks?.length > 0 && (
                                    <div className="mt-3 ps-3 border-start">
                                        <h6>{t('Linked Individuals')}</h6>
                                        <Table responsive className="compliance-links-table">
                                            <thead>
                                                <tr>
                                                    <th>{t('Full Name')}</th>
                                                    <th>{t('Relationship')}</th>
                                                    <th>{t('Ownership')} %</th>
                                                    <th className="compliance-links-table__id-col">{t('ID')}</th>
                                                    <th className="compliance-links-table__id-col">{t('Monitoring ID')}</th>
                                                    <th>{t('Datasets')}</th>
                                                </tr>
                                            </thead>
                                            <tbody>
                                                {business.individualLinks.map((ind, j) => (
                                                    <tr key={j}>
                                                        <td>
                                                            {[ind.firstName, ind.middleName, ind.lastName].filter(Boolean).join(" ")}
                                                        </td>
                                                        <td>{ind.relationship}</td>
                                                        <td>{ind.ownershipPercentage}</td>
                                                        <td className="compliance-links-table__id-col">
                                                            <CopyableText
                                                                value={ind.id}
                                                                maxLength={12}
                                                            />
                                                        </td>
                                                        <td className="compliance-links-table__id-col">
                                                            <CopyableText
                                                                value={ind.monitoringID}
                                                                maxLength={12}
                                                            />
                                                        </td>
                                                        <td>
                                                            {ind.datasets?.map((datasetKey, index) => {
                                                                return (
                                                                    <Badge
                                                                        key={index}
                                                                        id={`business-ind-row-dataset-${index}`}
                                                                        className="bg-info ms-1 me-1"
                                                                    >
                                                                        {t(KYBScreeningDataSets[datasetKey]?.title)}

                                                                        <UncontrolledTooltip
                                                                            target={`business-ind-row-dataset-${index}`}
                                                                        >
                                                                            {t(KYBScreeningDataSets[datasetKey]?.description)}
                                                                        </UncontrolledTooltip>
                                                                    </Badge>
                                                                )
                                                            })}
                                                        </td>
                                                    </tr>
                                                ))}
                                            </tbody>
                                        </Table>
                                    </div>
                                )}
                            </CardBody>
                        </Card>
                    ))
                ) : (
                    <div className="text-muted mb-3">
                        {t('No business links available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const GriEntries = ({ t, data: {
    griEntries: entries,
    monitoringID
}}) => {
    return (
        <Accordion.Item
            eventKey="10"
        >
            <Accordion.Header>
                <SectionHeader
                    t={t}
                    icon="file-text-line"
                    title="Galleries / Reports (GRI)"
                    count={entries?.length}
                    bgColor="success"
                />
            </Accordion.Header>

            <Accordion.Body
                accordionId="10"
            >
                {entries?.length > 0 ? (
                    <Table responsive striped>
                        <thead>
                            <tr>
                                <th
                                    style={{
                                        width: '140px'
                                    }}
                                >{t('Evidence ID')}</th>
                                <th>{t('Title')}</th>
                                <th>{t('Summary')}</th>
                                <th
                                    style={{
                                        width: '200px'
                                    }}
                                >{t('Keywords')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {entries.map((entry, i) => (
                                <tr key={i}>
                                    <td>{entry.evidenceID}</td>
                                    <td>{entry.title}</td>
                                    <td>{entry.summary}</td>
                                    <td>{entry.keywords}</td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : (
                    <div className="text-muted mb-3">
                        {t('No GRI entries available.')}
                    </div>
                )}

                <div>
                    <strong className="me-1">{t('Monitoring ID')}:</strong>
                    <CopyableText
                        value={monitoringID}
                        maxLength={20}
                    />
                </div>
            </Accordion.Body>
        </Accordion.Item>
    )
}

const DataAccrodionsRefs = {
    findings: (props) => <Evidences {...props} />,
    sanEntries: (props) => <SanctionEntries {...props} />,
    relEntries: (props) => <RelEntries {...props} />,
    rreEntries: (props) => <RreEntries {...props} />,
    poiEntries: (props) => <PoiEntries {...props} />,
    insEntries: (props) => <InsEntries {...props} />,
    ddEntries: (props) => <DdEntries {...props} />,
    pepEntries: (props) => <PepEntries {...props} />,
    individualLinks: (props) => <IndividualLinks {...props} />,
    businessLinks: (props) => <BusinessLinks {...props} />,
    griEntries: (props) => <GriEntries {...props} />
}

const sortAccordionsBasedOnContentLength = (data) => {
    const accordionsWithLength = [];

    [
        'relEntries',
        'rreEntries',
        'poiEntries',
        'insEntries',
        'ddEntries',
        'individualLinks',
        'businessLinks',
        'griEntries'
    ].forEach((itemKey) => {
        accordionsWithLength.push({
            keyName: itemKey,
            len: data[itemKey]?.length ?? 0
        });
    });

    accordionsWithLength.push({
        keyName: 'pepEntries',
        len: (data.pepEntries?.current?.length || 0) + (data.pepEntries?.former?.length || 0)
    });

    const sortedAccordions = accordionsWithLength.sort((a, b) => {
        if (a.len < b.len) return 1;
        if (a.len > b.len) return -1;
        return 0;
    });

    /**
     * move findings to the end of the list, and move sanEntries to the top of the list
     */
    sortedAccordions.push({
        keyName: 'findings',
        len: data.findings?.length ?? 0
    });

    sortedAccordions.unshift({
        keyName: 'sanEntries',
        len: (data.sanEntries?.current?.length || 0) + (data.sanEntries?.former?.length || 0)
    });

    return sortedAccordions;
}

const RiskAccordions = ({ t, data }) => {
    const sortedAccordions = sortAccordionsBasedOnContentLength(data);

    return (
        <Accordion
            flush
            alwaysOpen
            className="custom-accordion"
        >
            {sortedAccordions.map((accordionItem, index) => {
                const AccordionRef = DataAccrodionsRefs[accordionItem.keyName];
                if (!AccordionRef) return null;
                return (
                    <AccordionRef 
                        key={index}
                        t={t}
                        data={data}
                    />
                )
            })}
        </Accordion>
    );
}

export default withNamespaces()(
    RiskAccordions
)